import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { fmtLap, lapStats } from "@/lib/laps";

export const LapFileUpload = ({ sessionId, stint, lapData, onChange }) => {
  const { t } = useT();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const fromFile = lapData && lapData.source !== "telemetry";
  const theo = fromFile ? lapStats(lapData).theoretical : null;

  const send = async (name, content, force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${sessionId}/lap-file`, { filename: name, content }, { params: { stint, force } });
      const m = data.match;
      toast.success(m ? `${t("lapfile_done")} ${t("lapfile_match").replace("{m}", m.matched).replace("{n}", m.total)}` : t("lapfile_done"));
      onChange(data.session_update);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(formatApiError(err), { duration: 12000, action: { label: t("lapfile_force"), onClick: () => send(name, content, true) } });
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    send(file.name, await file.text());
  };

  return (
    <div data-testid={`lapfile-upload-${stint}`} className="flex flex-wrap items-center gap-3 bg-[#090A0F] border border-dashed border-[#A855F7]/40 rounded-sm p-4">
      <input ref={ref} type="file" accept=".csv,.CSV,text/csv,text/plain" className="hidden" data-testid={`lapfile-input-${stint}`} onChange={onFile} />
      {fromFile ? (
        <div className="flex items-center gap-2 font-mono text-sm min-w-0">
          <FileSpreadsheet className="w-4 h-4 text-[#A855F7] shrink-0" />
          <span data-testid="lapfile-name" className="truncate">{lapData.filename || "CSV"}</span>
          <span className="text-slate-500 text-xs">· {lapData.laps.length} {t("laps_short")}</span>
          {theo != null && <span data-testid="lapfile-theoretical" className="text-xs text-[#A855F7]">· {t("theoretical")} {fmtLap(theo, lapData.precision)}</span>}
          {theo == null && <span className="text-xs text-[#FFB800]">· {t("lapfile_no_sectors")}</span>}
        </div>
      ) : (
        <p className="text-sm text-slate-400">{t("lapfile_hint")}</p>
      )}
      <Button type="button" disabled={busy} data-testid={`lapfile-btn-${stint}`} onClick={() => ref.current?.click()}
        className="ml-auto rounded-sm bg-[#A855F7] hover:bg-[#9333EA] font-heading uppercase tracking-wide text-base h-10">
        {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileUp className="w-4 h-4 mr-1.5" />} {fromFile ? t("lapfile_replace") : t("lapfile_add")}
      </Button>
    </div>
  );
};
